import { CloseIcon, defaultTextVariants, IconButton, Text } from "@carbon6/c6-frontend-design";
import type { CSSProperties, ReactNode } from "react";
import { createPortal } from "react-dom";
import { useModalSurface } from "./useModalSurface";
import { RIGHT_SIDE_PANEL_Z } from "./windowLayers";
import styles from "./RightSidePanel.module.css";

/**
 * 우측 패널의 폭이다. 단위는 필드 이름으로 표시한다. `kind` 로 px 인지 % 인지 추측하지 않는다.
 * - `fixed`: 화면이 넓어져도 그대로다. 화면이 좁아지면 `max-width` 로 화면을 넘지 않게 한다.
 * - `flex`: 화면 비율에 따라 줄어든다. `minPx` 를 주면 그 값보다 작아지지 않는다.
 */
export type RightSidePanelWidth =
  | { kind: "fixed"; px: number }
  | { kind: "flex"; percent: number; minPx?: number };

export type RightSidePanelProps = {
  // 헤더 좌측 제목이다. accessible name(aria-label) 으로도 쓴다.
  title: string;
  // 우측 도킹 폭이다. side panel 은 항상 폭을 정해야 하므로 필수다.
  width: RightSidePanelWidth;
  // [X] 종료 요청이다. Escape 도 이 콜백에 매핑한다. 닫으면 팝업이 언마운트되고 상태가 폐기된다.
  onRequestClose: () => void;
  // Dialog 본문이다.
  children: ReactNode;
  // 닫기 버튼 왼쪽에 두는 페이지 액션 슬롯이다(예: 취소·주석). 선택.
  headerActions?: ReactNode;
  // 바깥(백드롭)을 클릭하면 호출한다. 지정하지 않으면 백드롭 클릭은 무시한다.
  onBackdrop?: () => void;
  // Accessible name 이 title 과 달라야 할 때 쓴다(선택). 기본값은 title 이다.
  ariaLabel?: string;
};

// 폭을 인라인 style 로 변환한다. 값이 호출부에서 오므로 CSS 클래스로는 표현할 수 없다.
function widthStyle(width: RightSidePanelWidth): CSSProperties {
  return width.kind === "fixed"
    ? // 화면보다 넓은 고정 폭이 와도 overlay 를 넘지 않도록 max-width 로 제한한다.
      { flex: "0 0 auto", width: `${width.px}px`, maxWidth: "100%" }
    : {
        flex: `0 1 ${width.percent}%`,
        minWidth: width.minPx === undefined ? undefined : `${width.minPx}px`,
      };
}

// 우측 도킹 패널 셸이다. 창 스택·작업표시줄 밖의 오버레이다. 마운트가 열림이므로 호출부가 조건부로
// 렌더한다. 닫으면 언마운트되고 상태가 폐기된다(최소화 없음). FullscreenWindow 처럼 c6 Modal 이
// 아니라 body portal 커스텀 오버레이다. c6 modalStack 에 직접 등록해 같은 규칙을 따른다:
// - scroll lock·바깥 inert 는 스택이 소유한다. 중첩 c6 Modal 과 ref-count/topmost 가 충돌하지 않는다.
// - z 는 작업표시줄(890)보다 높다. 열려 있는 동안 페이지·스택 창·작업표시줄이 모두 inert 가 되고
//   이 패널이 최상단이 된다(windowLayers.ts).
// - 패널 위 패널(예: 비교 위의 run 상세)은 별도 z 없이 **등록 순서**로 겹침을 정한다. modalStack 은
//   같은 z 에서 나중에 등록한 패널을 topmost 로 판정하고(동률 규칙), 먼저 등록된 패널을 inert 로 잠근다.
//   화면에서도 나중에 추가된 body 포털이 DOM 순서상 위에 그려진다.
// - dialog 를 popup mount node 로 등록한다. 그래서 패널 안 AnchoredPopup 계열이 dialog 안으로 portal 된다.
// - 중첩 c6 Modal 은 body 레벨 형제(z 1000~)이므로 이 오버레이 위에 쌓인다.
export function RightSidePanel({
  title,
  width,
  onRequestClose,
  children,
  headerActions,
  onBackdrop,
  ariaLabel,
}: RightSidePanelProps): ReactNode {
  const zIndex = RIGHT_SIDE_PANEL_Z;
  const { container, dialogRef, onBackdropClick, onBackdropMouseDown } = useModalSurface({
    active: true,
    rootAttributeName: "data-right-side-panel-root",
    zIndex,
    onRequestClose,
    onBackdrop,
  });

  if (container === null) {
    return null;
  }

  return createPortal(
    <div
      className={styles.overlay}
      style={{ zIndex }}
      // Scrim 은 표시용이다. 바깥 클릭은 onBackdrop(닫기) 편의 동작이다. 키보드는 Escape(종료)로 처리한다.
      role="presentation"
      onMouseDown={onBackdropMouseDown}
      onClick={onBackdropClick}
    >
      <div
        className={styles.dialog}
        style={widthStyle(width)}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? title}
        tabIndex={-1}
        ref={dialogRef}
      >
        <header className={styles.header}>
          <Text variant={defaultTextVariants["body-bold"]} className={styles.title}>
            {title}
          </Text>
          <div className={styles.headerRight}>
            {headerActions ? <div className={styles.headerActions}>{headerActions}</div> : null}
            <IconButton
              aria-label="종료"
              title="팝업을 닫습니다."
              type="button"
              variant="surface"
              size="sm"
              onClick={onRequestClose}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </header>
        <div className={styles.body}>{children}</div>
      </div>
    </div>,
    container,
  );
}
